import {Injectable, OnInit} from '@angular/core';
import {Action, select, Store} from '@ngrx/store';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {Dictionary, NgrxManagerAction, NgrxManagerConfig, NgrxManagerPropertyName, Request, RequestMethod, RequestType} from './model';
import * as actions from './actions';
import {StartSyncAction, StopSyncAction} from './actions';
import {
  getCommandFailed,
  getCommandQueue,
  getCommandStack,
  getQueryFailed,
  getQueryQueue,
  getQueryStack,
  getSyncActive,
  NgrxManagerState
} from './reducer';
import {HttpErrorResponse} from '@angular/common/http';
import {UuidService} from './providers/uuid.service';


@Injectable({
  providedIn: 'root'
})
export class NgrxManagerService implements OnInit {

  enabled$ = new BehaviorSubject(true);
  tokenDisable = 'x-disable-token';

  internetWirdGeprueft$ = new BehaviorSubject(false);
  letzeInternetPruefung$ = new BehaviorSubject(null);
  letzeInternetverbindung$ = new BehaviorSubject(null);

  // Nur diese Anfragen laden in Failed. Alles Anderen werden immer wieder probiert
  failedHttpStatusCodes = [400];
  activeSync = true;

  // GET
  queryStack: Dictionary<Request>;
  queryQueue: Dictionary<Request>;
  queryFailed: Array<Request>;

  // POST | PUT | DELETE
  commandStack: Dictionary<Request>;
  commandQueue: Array<Request>;
  commandFailed: Array<Request>;

  // STATUS
  commandStack$ = new Subject<Dictionary<Request>>();
  commandQueue$ = new Subject<Array<Request>>();
  commandFailed$ = new Subject<Array<Request>>();


  constructor(private store: Store<NgrxManagerState>,
              private uuidService: UuidService
  ) {
    // QUERY
    store.pipe(select(getQueryStack)).subscribe((item) => {
      this.queryStack = item;
    });

    store.pipe(select(getQueryQueue)).subscribe((item) => {
      this.queryQueue = item;
    });

    store.pipe(select(getQueryFailed)).subscribe((item) => {
      this.queryFailed = item;
    });

    // COMMAND
    store.pipe(select(getCommandStack)).subscribe((item) => {
      this.commandStack = item;
    });

    store.pipe(select(getCommandQueue)).subscribe((item) => {
      this.commandQueue = item;
    });

    store.pipe(select(getCommandFailed)).subscribe((item) => {
      this.commandFailed = item;
    });

    store.pipe(select(getSyncActive)).subscribe((item) => {
      this.activeSync = item;
    });
  }


  ngOnInit(): void {
    // Darf erst hier passieren, da sonst beim Neustart Anfragen verschluckt werden
    this.clearQueries();
  }

  public enableService() {
    this.enabled$.next(true);
  }

  public disableService() {
    this.enabled$.next(false);
  }

  public isEnabled$(): Observable<boolean> {
    return this.enabled$.asObservable();
  }

  public internetWirdGeprueft(): Observable<boolean> {
    return this.internetWirdGeprueft$.asObservable();
  }

  public letzeInternetPruefung(): Observable<Date | null> {
    return this.letzeInternetPruefung$.asObservable();
  }

  public letzeInternetverbindung(): Observable<Date | null> {
    return this.letzeInternetverbindung$.asObservable();
  }


  // Diese Funktion wird von allen Refresh's ausgelÃ¶st
  public clearQueries() {
    this.store.dispatch(new actions.CommandStackCleanUpAction());
    this.store.dispatch(new actions.QueryStackCleanUpAction());

    this.checkQueue();
  }

  // 1. Alle Client-NGRX Anfragen prÃ¼fen Ã¼ber diese Funktion erst einmal ob sie die Anfrage stellen dÃ¼rfen
  public checkRequestCall(key: string, action: Action | NgrxManagerAction, method: RequestMethod, type: RequestType): boolean | Action | NgrxManagerAction {
    const ngrxManagerConfig = (action.hasOwnProperty(NgrxManagerPropertyName)) ? <NgrxManagerConfig>action[NgrxManagerPropertyName] : null;
    // Soll Cache genutzt werden?
    if (!this.enabled$.getValue() || ngrxManagerConfig !== null && !ngrxManagerConfig.useCache) {
      return action;
    }
    let newAction = null;

    // Anfrage zum ersten Mal
    if (action.hasOwnProperty(NgrxManagerPropertyName) && ngrxManagerConfig === null || ngrxManagerConfig !== null && ngrxManagerConfig.traceId === undefined) {
      newAction = Object.assign({}, action, <NgrxManagerAction>{
        ngrxManager: Object.assign({}, ngrxManagerConfig, <NgrxManagerConfig>{
          traceId: this.uuidService.generateUUID(),
          firstRequest: new Date,
          lastRequest: new Date,
          failedRequests: 0,
          useCache: true
        })
      });
    } else if (ngrxManagerConfig !== null) {
      // Erneute Anfrage
      newAction = Object.assign({}, action, <NgrxManagerAction>{
        ngrxManager: Object.assign({}, ngrxManagerConfig, <NgrxManagerConfig>{
          lastRequest: new Date
        })
      });
    } else {
      // Action ist nicht vom Typ NgrxManagerAction
      newAction = action;
    }

    if (this.activeSync) {
      return this.onlineLogik(key, newAction, method, type);
    } else {
      return this.offlineLogik(key, newAction, method, type);
    }
  }

  // 2. Wenn die Client-NGRX Anfrage durchgefÃ¼hrt wurde (Erfolgreich oder Fehler) wird diese Info an diese Funktion Ã¼bergeben
  public checkRequestResult(key: string, action: Action | NgrxManagerAction, method: RequestMethod, type: RequestType, nextAction: Action | NgrxManagerAction, error: HttpErrorResponse | null = null): void {
    const ngrxManagerConfig = (action !== undefined && action.hasOwnProperty(NgrxManagerPropertyName)) ? <NgrxManagerConfig>action[NgrxManagerPropertyName] : null;
    // QUERY
    // -----
    if (method === RequestMethod.QUERY) {
      if (type === RequestType.Erfolgreich) {
        this.store.dispatch(new actions.InsightsQuerySuccess(action, nextAction));
        this.store.dispatch(new actions.QueryStackRemoveAction(key));
        this.store.dispatch(new actions.QueryQueueNextAction());
      }

      if (type === RequestType.Fehler) {
        // Wenn es schon der zweite Versuch ist, dann NgrxManager stoppen und prÃ¼fen ob es Internet gibt
        if (ngrxManagerConfig && ngrxManagerConfig.failedRequests > 0 && ((ngrxManagerConfig.failedRequests % 2) === 0)) {
          this.store.dispatch(new StopSyncAction());
          if (this.queryQueue.containsKey(key)) {
            this.store.dispatch(new actions.QueryQueueRemoveAction(key));
          }

          const newAction = Object.assign({}, action, <NgrxManagerAction>{
            ngrxManager: Object.assign({}, ngrxManagerConfig, {
              failedRequests: ngrxManagerConfig.failedRequests + 1,
              errorStatus: error.status,
              error: error
            })
          });
          this.offlineLogik(key, newAction, method, type);
          this.checkInternetConnection(true);
        } else {
          setTimeout(function () {
            const item = this.queryStack.item(key);

            if (item !== undefined && item.action !== undefined) {
              this.store.dispatch(new actions.QueryStackRemoveAction(item.key));

              // Action vom Typ NgrxManagerAction
              if (ngrxManagerConfig !== null) {
                const newAction = Object.assign({}, action, <NgrxManagerAction>{
                  ngrxManager: Object.assign({}, ngrxManagerConfig, {
                    failedRequests: ngrxManagerConfig.failedRequests + 1,
                    errorStatus: error.status,
                    error: error
                  })
                });


                if (this.failedHttpStatusCodes.indexOf(error.status) === -1) {
                  this.store.dispatch(new actions.InsightsQueryFailed(action, nextAction));
                  this.store.dispatch(newAction);
                } else {
                  this.store.dispatch(new actions.InsightsQueryFailedBadRequest(action, nextAction));
                  const resItem = Object.assign({}, item, <Request>{action: newAction, error: error});
                  this.store.dispatch(new actions.QueryFailedAddAction(resItem.key, resItem.action, resItem.method, resItem.type, resItem.date, resItem.error));
                  this.store.dispatch(new actions.QueryQueueNextAction());
                }
              }
            }
          }.bind(this), 3000);
        }
      }
    } else {
      // COMMAND
      // -------
      if (type === RequestType.Erfolgreich) {
        this.store.dispatch(new actions.InsightsCommandSuccess(action, nextAction));
        this.store.dispatch(new actions.CommandStackRemoveAction(key));
        this.store.dispatch(new actions.CommandQueueNextAction());
      }

      if (type === RequestType.Fehler) {
        // Wenn es schon der zweite Versuch ist, dann NgrxManager stoppen und prÃ¼fen ob es Internet gibt
        if (ngrxManagerConfig && ngrxManagerConfig.failedRequests > 0 && ((ngrxManagerConfig.failedRequests % 2) === 0)) {
          this.store.dispatch(new StopSyncAction());

          const newAction = Object.assign({}, action, <NgrxManagerAction>{
            ngrxManager: Object.assign({}, ngrxManagerConfig, {
              failedRequests: ngrxManagerConfig.failedRequests + 1,
              errorStatus: error.status,
              error: error
            })
          });

          // this.store.dispatch(new actions.CommandQueueAddAction(key, newAction, method, type, new Date()));
          // this.store.dispatch(new actions.CommandStackCleanUpAction()); // aufrÃ¤umen, damit der Request vorne einsortiert wird
          // this.checkInternetConnection(true);
          this.offlineLogik(key, newAction, method, type);
          this.checkInternetConnection(true);
        } else {
          setTimeout(function () {
            const item = this.commandStack.item(key);

            if (item !== undefined && item.action !== undefined) {
              this.store.dispatch(new actions.CommandStackRemoveAction(item.key));

              // Action vom Typ NgrxManagerAction
              if (ngrxManagerConfig !== null) {
                const newAction = Object.assign({}, action, <NgrxManagerAction>{
                  ngrxManager: Object.assign({}, ngrxManagerConfig, {
                    failedRequests: ngrxManagerConfig.failedRequests + 1,
                    errorStatus: error.status,
                    error: error
                  })
                });

                if (this.failedHttpStatusCodes.indexOf(error.status) === -1) {
                  this.store.dispatch(new actions.InsightsCommandFailed(action, nextAction));
                  this.store.dispatch(newAction);
                } else {
                  this.store.dispatch(new actions.InsightsCommandFailedBadRequest(action, nextAction));
                  const resItem = Object.assign({}, item, <Request>{action: newAction, error: error});
                  this.store.dispatch(new actions.CommandFailedAddAction(resItem.key, resItem.action, resItem.method, resItem.type, resItem.date, resItem.error));
                  this.store.dispatch(new actions.CommandQueueNextAction());
                }
              }
            }
          }.bind(this), 3000);
        }
      }
    }
  }

  public async checkInternetConnection(autoStartActiveSync = false) {
    const time = new Date();
    const hash = time.getFullYear() + time.getMonth() + time.getDate() + time.getHours() + time.getMinutes() + time.getSeconds() + time.getMilliseconds();
    const options = {
      method: 'GET',
      url: 'https://www.softwarepioniere.de?' + hash,
      headers: {
        'content-type': 'text/plain',
        'Accept': '*/*',
        'Authorization': this.tokenDisable,
        'Cache-Control': 'no-cache, no-store, must-revalidate'
      }
    };

    // NUR BEI INTERNETVERBINDUNG ZUM LOGIN WEITER
    const xhr = new XMLHttpRequest();
    xhr.open('GET', options.url, true);
    xhr.timeout = 3000;
    xhr.onload = function () {
      this.internetWirdGeprueft$.next(false);
      this.letzeInternetPruefung$.next(new Date());
      // do something to response
      if (xhr.responseText !== undefined) {
        this.letzeInternetverbindung$.next(new Date());
        if (autoStartActiveSync) {
          this.store.dispatch(new StartSyncAction(false));
        }
      } else {
        this.store.dispatch(new StopSyncAction());
        // KEIN INTERNET
      }
    }.bind(this);

    xhr.onabort = function () {
      this.internetWirdGeprueft$.next(false);
      this.letzeInternetPruefung$.next(new Date());
      this.store.dispatch(new StopSyncAction());
    }.bind(this);

    xhr.ontimeout = function () {
      this.internetWirdGeprueft$.next(false);
      this.letzeInternetPruefung$.next(new Date());
      this.store.dispatch(new StopSyncAction());
    }.bind(this);

    xhr.onerror = function (err) {
      this.internetWirdGeprueft$.next(false);
      this.letzeInternetPruefung$.next(new Date());
      this.store.dispatch(new StopSyncAction());
    }.bind(this);

    this.internetWirdGeprueft$.next(true);
    xhr.send();
  }

  private checkQueue() {
    this.store.dispatch(new actions.CommandQueueNextAction());
    this.store.dispatch(new actions.QueryQueueNextAction());
  }

  private offlineLogik(key: string, action: Action, method: RequestMethod, type: RequestType): boolean {
    if (method === RequestMethod.QUERY) {
      // QUERY
      // -----
      this.store.dispatch(new actions.InsightsQueryNotExecuted(action));

      if (this.queryStack.values().length > 0) {
        this.store.dispatch(new actions.QueryStackCleanUpAction());
      }

      // 2. Wenn die Anfrage noch nicht in der Queue ist wird sie sich gemerkt
      if (!this.queryQueue.containsKey(key)) {
        this.store.dispatch(new actions.QueryQueueAddAction(key, action, method, type, new Date()));
      }
    } else {
      // COMMAND
      // -------
      this.store.dispatch(new actions.InsightsCommandNotExecuted(action));

      if (this.commandStack.values().length > 0) {
        this.store.dispatch(new actions.CommandStackCleanUpAction());
      }

      this.store.dispatch(new actions.CommandQueueAddAction(key, action, method, type, new Date()));
    }

    return false;
  }

  private onlineLogik(key: string, action: Action, method: RequestMethod, type: RequestType): boolean | Action | NgrxManagerAction {
    if (method === RequestMethod.QUERY) {
      // QUERY
      // -----

      // INFO:
      // GET Anfragen mÃ¼ssen nicht gesammelt werden. Es reicht, wenn es die Aktuelle gibt und
      // man weiÃ, wenn es danach nochmal angefagt werden soll
      // 1. Anfrage noch nicht auf Stack
      if (!this.queryStack.containsKey(key)) {
        this.store.dispatch(new actions.InsightsQueryExecuted(action));
        this.store.dispatch(new actions.QueryStackAddAction(key, action, method, type, new Date()));
        return action;
      } else if (!this.queryQueue.containsKey(key)) {
        // 2. Anfrage schon auf Stack und noch nicht in der Queue
        this.store.dispatch(new actions.InsightsQueryNotExecuted(action));
        this.store.dispatch(new actions.QueryQueueAddAction(key, action, method, type, new Date()));
      }
      return false;
    } else {
      // COMMAND
      // -------

      // INFO:
      // POST, PUT or DELETE must always save

      // 1. a command currently on stack
      if (this.commandStack.values().length === 0) {
        this.store.dispatch(new actions.InsightsCommandExecuted(action));
        this.store.dispatch(new actions.CommandStackAddAction(key, action, method, type, new Date()));
        return action;
      } else {
        this.store.dispatch(new actions.InsightsCommandNotExecuted(action));
        this.store.dispatch(new actions.CommandQueueAddAction(key, action, method, type, new Date()));
        return false;
      }
    }
  }
}
